///////////////////
//
///////////////////
import {
    OnInit,
    Component,
    Injector,
    Input,
    Output,
    Inject,
    AfterViewInit,
    AfterContentInit,
    EventEmitter,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { GridComponent } from "@progress/kendo-angular-grid";
import { LocalStorageService } from "../../../core/local-storage.service";
import { ScreenSizeService } from "../../services/screen-size.service";
import { OutputType } from "@angular/core/src/view";

const Operators = {
    none: [],
    string: [
        {
            operator: "contains",
            translation: "kendo.grid.filterContainsOperator",
        },
    ],
    number: [
        { operator: "eq", translation: "kendo.grid.filterEqOperator" },
        { operator: "lt", translation: "kendo.grid.filterLtOperator" },
        { operator: "lte", translation: "kendo.grid.filterLteOperator" },
        { operator: "gt", translation: "kendo.grid.filterGtOperator" },
        { operator: "gte", translation: "kendo.grid.filterGteOperator" },
    ],
    date: [
        { operator: "eq", translation: "kendo.grid.filterEqOperator" },
        { operator: "lt", translation: "kendo.grid.filterBeforeOperator" },
        {
            operator: "lte",
            translation: "kendo.grid.filterBeforeOrEqualOperator",
        },
        { operator: "gt", translation: "kendo.grid.filterAfterOperator" },
        {
            operator: "gte",
            translation: "kendo.grid.filterAfterOrEqualOperator",
        },
    ],
};

const noField = {
    field: "",
    translation: "",
    type: "none",
};

const noOperator = null;

@Component({
    selector: "djiv-filter-menu",
    templateUrl: "./djiv-filter-menu.component.html",
    styleUrls: ["./djiv-filter-menu.component.css"],
})
export class DJIVFilterMenu implements OnInit, AfterViewInit {
    @Input() fields: [
        {
            field: string;
            translation: string;
            type: string;
            enum?: { operator: string; translation: string }[];
        }
    ];
    @Input() grid?: GridComponent;
    @Output() filterFields = new EventEmitter<any>();

    protected selectedField: {
        field: string;
        translation: string;
        type: string;
        enum?: { operator: string; translation: string }[];
    } = noField;
    protected selectedOperator: { operator: string; translation: string } =
        noOperator;
    protected value = "";

    private localStorage: LocalStorageService;
    private isMobile: boolean = false;
    private screenSizeService: ScreenSizeService;

    get operators() {
        if (this.isEnumSelectedField()) return this.selectedField.enum;

        return Operators[this.selectedField.type];
    }

    get valueType() {
        switch (this.selectedField.type) {
            case "number":
                return "number";
            case "date":
                return "date";
            default:
                return "text";
        }
    }

    constructor(
        @Inject(Injector) injector: Injector,
        protected translate: TranslateService
    ) {
        this.localStorage = injector.get(LocalStorageService);
        this.screenSizeService = injector.get(ScreenSizeService);
    }

    ngOnInit() {
        this.isMobile = this.screenSizeService.isMobile;
        this.screenSizeService.isMobile$.subscribe(
            (response) => (this.isMobile = response)
        );
    }

    ngAfterViewInit() {
        this.fetchFiltersFromCache();
    }

    fetchFiltersFromCache() {
        const storageField = this.localStorage.getItem("bookingFilterField");
        const storageFieldType = this.localStorage.getItem(
            "bookingFilterFieldType"
        );
        const storageOperator = this.localStorage.getItem(
            "bookingFilterOperator"
        );
        // const storageValue = this.localStorage.getItem("bookingFilterValue");
        const storageValue =
            storageFieldType === "date"
                ? new Date(this.localStorage.getItem("bookingFilterValue"))
                : this.localStorage.getItem("bookingFilterValue");

        if (!!storageField && !!storageOperator) {
            const field = this.fields.find(
                (f) =>
                    f.field === storageField.field &&
                    f.translation === storageField.translation &&
                    f.type === storageField.type
            );

            this.selectedField = field || noField;

            const operator = this.operators.find(
                (op) =>
                    op.operator === storageOperator.operator &&
                    op.translation === storageOperator.translation
            );

            this.selectedOperator = operator || noOperator;

            this.value = storageValue;

            this.filter();
        }
    }

    storeFiltersInCache() {
        this.localStorage.setItem("bookingFilterField", this.selectedField);
        this.localStorage.setItem(
            "bookingFilterFieldType",
            this.selectedField.type
        );
        this.localStorage.setItem(
            "bookingFilterOperator",
            this.selectedOperator
        );
        this.localStorage.setItem("bookingFilterValue", this.value);
    }

    clearFiltersInCache() {
        this.localStorage.removeItem("bookingFilterField");
        this.localStorage.removeItem("bookingFilterFieldType");
        this.localStorage.removeItem("bookingFilterOperator");
        this.localStorage.removeItem("bookingFilterValue");
    }

    isStringField() {
        return this.selectedField.type === "string";
    }

    isEnumSelectedField() {
        return this.selectedField.type === "enum";
    }

    isNumericField() {
        return this.selectedField.type === "number";
    }

    isDateField() {
        return this.selectedField.type === "date";
    }

    onFieldChange() {
        this.selectedOperator =
            this.operators.length > 0 ? this.operators[0] : null;
        this.value = "";
    }

    clearFilter() {
        if (this.isMobile) {
            this.filterFields.emit({
                filters: [],
                logic: "and",
            });
        } else {
            this.grid.filterChange.emit({
                filters: [],
                logic: "and",
            });
        }

        this.selectedField = noField;
        this.selectedOperator = null;
        this.value = "";

        this.clearFiltersInCache();
    }

    validateFilter() {
        return (
            this.selectedField.field &&
            this.selectedOperator &&
            (this.selectedField.type === "enum" || this.value)
        );
    }

    filter() {
        if (!this.validateFilter()) return;
        if (this.isMobile) {
            this.filterFields.emit({
                filters: [
                    {
                        field: this.selectedField.field,
                        operator: this.parseOperator(),
                        value: this.parseValue(),
                    },
                ],
                logic: "and",
            });
        } else {
            this.grid.filterChange.emit({
                filters: [
                    {
                        field: this.selectedField.field,
                        operator: this.parseOperator(),
                        value: this.parseValue(),
                    },
                ],
                logic: "and",
            });
        }

        this.storeFiltersInCache();
    }

    parseOperator() {
        if (this.selectedField.type === "enum") return "eq";
        return this.selectedOperator.operator;
    }

    parseValue() {
        if (this.selectedField.type === "enum")
            return this.selectedOperator.operator;
        return this.value;
    }
}



// WEBPACK FOOTER //
// C:/Repositories/wfa3/SupplierPortal/app/src/app/shared/components/djiv-filter-menu/djiv-filter-menu.component.ts